import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQ_ITEMS, FAQ_CATEGORIES } from "@/lib/data";
import {
  Rocket,
  Calendar,
  Shield,
  Wallet,
  FileText,
  Landmark,
  Phone,
  Search,
} from "lucide-react";

const ICONS = { Rocket, Calendar, Shield, Wallet, FileText, Landmark, Phone };

const FAQ = () => {
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    let list = FAQ_ITEMS;
    if (activeCat !== "all") list = list.filter((it) => it.cat === activeCat);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (it) =>
          it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeCat, query]);

  return (
    <section className="py-20 lg:py-24 bg-white" data-testid="faq-section">
      <div className="container-tm">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 mb-12 items-end">
          <div className="lg:col-span-7">
            <span className="overline">Questions fréquentes</span>
            <h2 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Avez-vous d'autres
              <span className="italic text-gold"> questions</span> ?
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="font-sans text-base text-ink/65 leading-relaxed">
              {FAQ_ITEMS.length} questions de propriétaires Airbnb, regroupées
              par thème. Cliquez sur une catégorie ou recherchez directement
              votre question.
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="max-w-xl mx-auto mb-8">
          <div className="relative">
            <Search
              size={18}
              className="absolute left-5 top-1/2 -translate-y-1/2 text-ink/40 pointer-events-none"
            />
            <input
              type="text"
              data-testid="faq-search"
              placeholder="Rechercher : enregistrement, taxe, caution, ménage…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-12 pr-5 py-3.5 rounded-full border border-gold/30 bg-cream/50 focus:bg-white focus:border-gold focus:outline-none transition-colors text-ink placeholder:text-ink/40 text-sm"
            />
          </div>
        </div>

        {/* Category pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          <button
            type="button"
            data-testid="faq-cat-all"
            onClick={() => setActiveCat("all")}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 border ${
              activeCat === "all"
                ? "bg-ink text-cream border-ink"
                : "bg-white text-ink/70 border-gold/30 hover:border-gold hover:text-ink"
            }`}
          >
            Toutes ({FAQ_ITEMS.length})
          </button>
          {FAQ_CATEGORIES.map((c) => {
            const Icon = ICONS[c.icon];
            const count = FAQ_ITEMS.filter((it) => it.cat === c.id).length;
            const active = activeCat === c.id;
            return (
              <button
                key={c.id}
                type="button"
                data-testid={`faq-cat-${c.id}`}
                onClick={() => setActiveCat(c.id)}
                className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 border ${
                  active
                    ? "bg-ink text-cream border-ink"
                    : "bg-white text-ink/70 border-gold/30 hover:border-gold hover:text-ink"
                }`}
              >
                {Icon && <Icon size={14} strokeWidth={1.8} />}
                {c.label}
                <span className={`text-xs ${active ? "text-gold" : "text-ink/40"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Accordion */}
        <div className="max-w-4xl mx-auto">
          {filtered.length === 0 ? (
            <p className="text-center text-ink/55 py-16">
              Aucune question ne correspond à votre recherche.
            </p>
          ) : (
            <Accordion
              type="single"
              collapsible
              className="w-full"
              data-testid="faq-accordion"
            >
              {filtered.map((item, i) => {
                const cat = FAQ_CATEGORIES.find((c) => c.id === item.cat);
                return (
                  <AccordionItem
                    key={`${item.cat}-${i}`}
                    value={`item-${item.cat}-${i}`}
                    data-testid={`faq-item-${item.cat}-${i}`}
                    className="border-b border-ink/10 first:border-t"
                  >
                    <AccordionTrigger className="text-left font-serif text-lg md:text-xl text-ink hover:text-gold py-5 hover:no-underline">
                      <div className="flex items-start gap-3 pr-4">
                        {cat && (
                          <span className="hidden md:inline-block text-[10px] uppercase tracking-[0.18em] text-gold/80 font-semibold mt-1.5 shrink-0 w-32">
                            {cat.label}
                          </span>
                        )}
                        <span>{item.q}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="font-sans text-base text-ink/70 leading-relaxed pb-6 md:pl-36">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
